import { useAuth } from '@teachino/shared-auth';
import { $t } from '@teachino/shared-i18n';
import {
  InfrastructureCommandBuilder,
  type InfrastructureCommand,
} from '@teachino/shared-infrastructure';
import { useGlobalDependencyProvider } from '@teachino/shared-util';
import { toValue, type MaybeRef, type Ref } from 'vue';
import type { ImageResult } from './image-result';
import { imageSearchResultArraySchema } from './image-search-result';
import type { ImageSearchFacade } from './image-search.facade';
import * as z from 'zod';

export class ImageSearchService implements ImageSearchFacade {
  searchByKeywords(
    keywords: MaybeRef<string>,
  ): InfrastructureCommand<ImageResult[]> {
    const searchImagesFetchFn = async () => {
      //analytics.track('image-selector-image-search-started');
      const auth = useAuth();
      const globalDi = useGlobalDependencyProvider();
      const config = globalDi.inject('config');

      const response = await auth.fetchWithAuth(
        `${config.baseUrl()}/image-search/results?q=${encodeURIComponent(toValue(keywords))}`,
        {
          method: 'GET',
          headers: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response.ok) {
        throw new Error($t('generic_error'));
      }

      const jsonData: unknown = await response.json();
      const parsedRes = imageSearchResultArraySchema.safeParse(jsonData);

      if (!parsedRes.success) {
        throw new Error($t('generic_error'));
      }

      return parsedRes.data;
    };

    return InfrastructureCommandBuilder.fromFetch(searchImagesFetchFn)
      .withMapper((result) => {
        return result.map((image) => {
          const normalizedImageName = image.imageName?.trim();
          const fallbackOriginalName =
            image.downloadUrl.split('/').pop() ?? 'image';

          return {
            url: image.downloadUrl,
            key: `search-${Date.now().toString()}-${Math.random().toString()}`,
            originalName:
              normalizedImageName && normalizedImageName.length > 0
                ? normalizedImageName
                : fallbackOriginalName,
            previewUrl: image.previewUrl,
            source: {
              sourceName: image.sourceName,
              sourceUrl: image.sourceUrl,
              authorName: image.creator ?? undefined,
              authorUrl: image.creatorUrl ?? undefined,
            },
          };
        });
      })
      .build();
  }

  uploadImage(
    image: Ref<ImageResult | null>,
  ): InfrastructureCommand<{ url: string }> {
    const auth = useAuth();
    const globalDi = useGlobalDependencyProvider();
    const config = globalDi.inject('config');

    const uploadSchema = z.object({
      url: z.string(),
    });

    const uploadImageFetchFn = async () => {
      if (!image.value?.url) {
        throw new Error($t('generic_error'));
      }
      const uploadImageFromUrlUrl = `${config.baseUrl()}/upload/uploadImageFromUrl`;

      const response = await auth.fetchWithAuth(uploadImageFromUrlUrl, {
        method: 'POST',
        headers: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileUrl: image.value.url,
        }),
      });

      if (!response.ok) {
        throw new Error($t('generic_error'));
      }

      const jsonData: unknown = await response.json();
      const parsedRes = uploadSchema.safeParse(jsonData);

      if (!parsedRes.success) {
        throw new Error($t('generic_error'));
      }
      return parsedRes.data;
    };

    return InfrastructureCommandBuilder.fromFetch(uploadImageFetchFn)
      .withMapper((r) => r)
      .build();
  }
}
